import type { TagItem } from 'components/shared/Tags';
import { SgTagsPicker } from 'components/shared/Tags';
import type { Custodian } from 'models/custodian';
import { FC, useCallback, useMemo } from 'react';

import { FeedbackMessage } from '../FeedbackMessage';

export type CustodiansFieldProps = {
  custodians?: ReadonlyArray<Custodian> | undefined;
  selectedBdrId?: string | null;
  onChange: (custodianBdrId?: string) => void;
  disabled: boolean;
  className?: string;
  readonly?: boolean;
};

const formatCustodianLabel = (custodian: Custodian) => {
  return `${custodian.label} (${[custodian.id, custodian.eliotCode].filter(v => !!v).join(' - ')})`;
};
export const CustodiansField: FC<CustodiansFieldProps> = ({
  custodians,
  onChange,
  disabled,
  selectedBdrId,
  className,
  readonly = false,
}) => {
  const onSingleSelect = useCallback(
    (selection: readonly string[]) => {
      if (selection?.length === 1) {
        onChange(selection[0]);
      }
    },
    [onChange],
  );
  const onRemoveAll = useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  const available = useMemo(
    () =>
      (custodians
        ? custodians.map(custodian => ({ label: formatCustodianLabel(custodian), key: custodian.id }))
        : undefined) ?? [],
    [custodians],
  );

  const singleSelection: string[] = useMemo(() => {
    if (selectedBdrId) {
      return custodians?.filter(c => c.id === selectedBdrId)?.map(c => c.id) ?? [];
    }
    return [];
  }, [selectedBdrId, custodians]);

  const hasSelectedMissingCustodian = !!selectedBdrId && singleSelection?.length === 0;

  const missing: TagItem = {
    key: selectedBdrId!,
    label: `BDR ${selectedBdrId}`,
    forced: true,
  };
  return (
    <>
      <SgTagsPicker
        id="custodians-field-picker"
        onSelectionKeysChanged={onSingleSelect}
        onRemoveAll={onRemoveAll}
        singleSelect
        className={className}
        noIcon={readonly || false}
        selection={hasSelectedMissingCustodian ? [missing.key] : singleSelection}
        available={hasSelectedMissingCustodian ? [...available, missing] : available}
        disabled={disabled}
        maxDisplayedItems={available.length}
        noClear={readonly || false}
        placeholder="Custodian"
        hasTooltip
      />
      {hasSelectedMissingCustodian && (
        <FeedbackMessage labelKey="secondaryMarket.errors.custodian.incoherent" color="info" />
      )}
    </>
  );
};

CustodiansField.displayName = 'CustodiansField';
